import { useEffect, useState } from "react";
import { getReports, API } from "@/lib/api";
import { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp, FileText, Wallet, FileCheck, Download } from "lucide-react";

export default function Reports() {
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const load = () => getReports(start || undefined, end || undefined).then(setData).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = (kind) => { window.open(`${API}/reports/export?kind=${kind}`, "_blank"); };

  if (!data) return <Loading />;

  const stats = [
    { label: "Total Invoiced", value: money(data.total_invoiced), icon: FileText, cls: "bg-slate-100 text-[#0B132B]" },
    { label: "Total Received", value: money(data.total_received), icon: Wallet, cls: "bg-emerald-50 text-emerald-600" },
    { label: "Invoices", value: data.invoice_count, icon: FileText, cls: "bg-blue-50 text-blue-600" },
    { label: "Quotes Value", value: money(data.quote_value), icon: FileCheck, cls: "bg-amber-50 text-amber-600" },
  ];

  return (
    <div>
      <PageHeader title="Reports" subtitle="Analyse sales, revenue and customer balances.">
        <Button variant="outline" onClick={() => exportCsv("invoices")} data-testid="export-invoices-csv"><Download className="w-4 h-4 mr-1.5" /> Invoices CSV</Button>
        <Button variant="outline" onClick={() => exportCsv("payments")} data-testid="export-payments-csv"><Download className="w-4 h-4 mr-1.5" /> Payments CSV</Button>
      </PageHeader>

      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div><Label>From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5" data-testid="report-start-date" /></div>
        <div><Label>To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5" data-testid="report-end-date" /></div>
        <Button onClick={load} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="apply-report-filter"><TrendingUp className="w-4 h-4 mr-1.5" /> Apply Filter</Button>
        {(start || end) && <Button variant="ghost" onClick={() => { setStart(""); setEnd(""); setTimeout(load, 0); }} data-testid="clear-report-filter">Clear</Button>}
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5" data-testid={`report-stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
            <div className={`w-10 h-10 rounded-lg ${s.cls} flex items-center justify-center`}><s.icon className="w-5 h-5" /></div>
            <div className="mt-4 font-display text-2xl font-bold text-[#0B132B]">{s.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-5 border-b border-slate-100"><h3 className="font-display font-semibold text-[#0B132B]">Customer Balances</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100"><th className="px-5 py-3 font-semibold">Customer</th><th className="px-5 py-3 font-semibold text-right">Billed</th><th className="px-5 py-3 font-semibold text-right">Outstanding</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.customer_balances.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-400">No data</td></tr>}
                {data.customer_balances.map((c, i) => (
                  <tr key={i}><td className="px-5 py-3 text-[#0B132B]">{c.name}</td><td className="px-5 py-3 text-right text-slate-600">{money(c.billed)}</td><td className="px-5 py-3 text-right font-semibold text-[#0B132B]">{money(c.outstanding)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between"><h3 className="font-display font-semibold text-[#0B132B]">Payments in Period</h3><span className="text-sm font-semibold text-emerald-600">{money(data.total_received)}</span></div>
          <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100"><th className="px-5 py-3 font-semibold">Date</th><th className="px-5 py-3 font-semibold">Invoice</th><th className="px-5 py-3 font-semibold text-right">Amount</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.payments.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-400">No payments</td></tr>}
                {data.payments.map((p, i) => (
                  <tr key={i}><td className="px-5 py-3 text-slate-600">{fmtDate(p.date)}</td><td className="px-5 py-3 text-[#0B132B]">{p.invoice_number}</td><td className="px-5 py-3 text-right font-semibold text-emerald-600">{money(p.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
